import Button from '@components/Button';
import Stack, { type StackProps } from '@components/Stack';
import Typography from '@components/Typography';

interface QuoteErrorRetryProps {
    /** Copy set by the quote saga (`cart.quoteError`). */
    message: string;
    /** Re-dispatches the quote request, or runs `actionLabel`'s action when one is given. */
    onRetry: () => void;
    /**
     * Replaces the Retry label when a re-quote cannot fix the failure, e.g. "Choose location"
     * for a bag with no selected store (JSAD-7345). `onRetry` then performs that action.
     */
    actionLabel?: string;
    sx?: StackProps['sx'];
}

/**
 * Inline replacement for the order totals when a quote attempt ended without a quote
 * (JSAD-7263). Before this, every silent quote failure — a 2xx body that is not a quote, a
 * parser throw, a missing schedule store — left checkout with no totals and a grey Pay
 * button that explained nothing; the user's only option was to leave. Cross-platform: the
 * web checkout and the native checkout screen both render it in the totals slot.
 */
const QuoteErrorRetry: React.FC<QuoteErrorRetryProps> = ({ message, onRetry, actionLabel, sx }) => {
    return (
        <Stack testID="quote-error" center gap={12} sx={sx}>
            <Typography testID="quote-error-message" variant="p3semi" color="red" center>
                {message}
            </Typography>
            <Button
                testID="quote-error-retry"
                variant="secondary.small"
                onPress={onRetry}
                accessibilityLabel={actionLabel ?? 'Retry calculating your order total'}
                accessibilityHint={
                    actionLabel ? 'Opens location selection' : 'Requests your order total again'
                }
                accessibilityRole="button"
            >
                {actionLabel ?? 'Retry'}
            </Button>
        </Stack>
    );
};

export default QuoteErrorRetry;
